import { useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, CircularProgress, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import { useController } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";

// import ContentRepositoryView
import api from 'services/api/campaign';
import ContentRepositoryView from "pages/views/ContentRepositoryView";
import SoftTypography from "components/SoftComponents/SoftTypography";

// --- NEW: Independent Preview Component ---
const ContentPreview = ({ contentId }) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['imagePreview', contentId],
        queryFn: () => api.getContentFile(contentId),
        enabled: !!contentId, // Only run the query if we actually have an ID
        staleTime: 5 * 60 * 1000,
    });

    if (!contentId) return null;

    if (isLoading) {
        return (
            <SoftBox display="flex" justifyContent="center" alignItems="center" height="150px">
                <CircularProgress sx={{ color: '#cb0c9f' }} />
            </SoftBox>
        );
    }

    if (isError || !data?.signedUrl) {
        return (
            <SoftBox p={2} border="1px dashed #ccc" borderRadius="8px" textAlign="center">
                <Typography variant="caption" color="error">Preview unavailable</Typography>
            </SoftBox>
        );
    }

    return (
        <SoftBox
            component="img"
            src={data.signedUrl}
            alt="Content preview"
            mb={1}
            sx={{
                objectFit: 'contain',
                maxHeight: '400px',
                maxWidth: '100%',
                borderRadius: '8px',
                // boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}
        />
    );
};

// --- MAIN COMPONENT ---
const MediaSelector = ({ control }) => {
    const [modalOpen, setModalOpen] = useState(false);

    // Bind directly to the field your DB expects
    const { field } = useController({
        control,
        name: 'contentId'
    });

    const handleSelectFromRepo = (selectedItem) => {
        // Just store the raw string ID in the form state!
        field.onChange(selectedItem._id);
        setModalOpen(false);
    };

    return (
        <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" px={1}>
                <SoftTypography pb={{xxs: 1, xs: 0}} variant="subtitle2" fontWeight="bold">{field.value ? "CONTENT SELECTED" : "NO CONTENT"}</SoftTypography>
                <SoftButton
                    color="dark"
                    variant="gradient"
                    onClick={() => setModalOpen(true)}
                >
                    {field.value ? "Change Media" : "Select Media"}
                </SoftButton>
            </SoftBox>

            {/* Let the independent component handle the heavy lifting of fetching and displaying */}
            {field.value && (
                <SoftBox pt={3} display="flex" flexDirection="column">
                    <ContentPreview contentId={field.value} />
                </SoftBox>
            )}

            {/* Content Repository Modal */}
            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                maxWidth="xl"
                fullWidth
                PaperProps={{ sx: { height: '80vh', borderRadius: '1rem' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', m: 0, p: 2 }}>
                    Select Media from Repository
                    <IconButton onClick={() => setModalOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, backgroundColor: '#f8f9fa' }}>
                    <ContentRepositoryView
                        isModal={true}
                        onSelect={handleSelectFromRepo}
                    />
                </DialogContent>
            </Dialog>
        </SoftBox>
    );
};

export default MediaSelector;